import { useId } from 'react';

/**
 * Infinite marquee of "success story" cards styled like the funnel 28
 * (MomApply) hero strip: a full-bleed photo with 16px corners and a frosted
 * white caption panel (name + bullet lines) rendered in HTML, so copy and
 * colours live in code instead of being baked into the image.
 *
 * Desktop: `columns` vertical tracks scrolling in lockstep (`direction`, down by
 * default like the funnel 28 strip), the first/left column shifted by half a
 * card so the grid reads as a staggered masonry (set `alternate` to scroll
 * neighbouring columns in opposite directions).
 * Below `mobileBreakpoint`: one horizontal track. Any number of stories works;
 * they are dealt round-robin into the columns and each track is duplicated so
 * the -50% translate loops seamlessly.
 *
 * @typedef {{ id?: string|number, image: string, name: string, lines?: string[], background?: string, textColor?: string, alt?: string }} Story
 */
const StoryCard = ({ story, width, height, defaultBackground, defaultTextColor, hidden, compact }) => (
  <figure
    className="relative m-0 shrink-0 overflow-hidden rounded-2xl bg-[#E9EEF6]"
    style={{ width, height }}
    aria-hidden={hidden || undefined}
  >
    <img
      src={story.image}
      alt={hidden ? '' : story.alt || `${story.name} success story`}
      loading="lazy"
      className="absolute inset-0 block h-full w-full object-cover object-top"
    />
    <figcaption
      className={`absolute rounded-[10px] backdrop-blur-md ${
        compact ? 'inset-x-2.5 bottom-2.5 px-3 pb-2.5 pt-2' : 'inset-x-3 bottom-3 px-3.5 pb-3 pt-2.5'
      }`}
      style={{ background: story.background || defaultBackground, color: story.textColor || defaultTextColor }}
    >
      <div className={`font-inter font-semibold leading-[1.3] ${compact ? 'text-[15px]' : 'text-[17px]'}`}>
        {story.name}
      </div>
      {story.lines?.length > 0 && (
        <ul
          className={`m-0 mt-1 list-disc font-inter ${
            compact ? 'pl-3.5 text-[12px] leading-[1.5]' : 'pl-4 text-[14px] leading-[1.55]'
          }`}
        >
          {story.lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </figcaption>
  </figure>
);

const StoryMarquee = ({
  stories = [],
  columns = 2,
  cardWidth = 288,
  cardHeight = 372,
  gap = 20,
  columnGap = 20,
  height = 'min(80vh, 760px)',
  // Scroll speed in px/s. Defaults match the funnel 28 hero strip, whose
  // background-position animation moves ~92px/s on desktop and ~85px/s on mobile.
  columnSpeed = 92,
  rowSpeed = 85,
  direction = 'down',
  alternate = false,
  stagger = true,
  fade = false,
  mobileBreakpoint = 1080,
  mobileCard = { width: 246, height: 292 },
  mobileGap = 14,
  defaultBackground = 'rgba(255, 255, 255, 0.75)',
  defaultTextColor = '#333333',
  ariaLabel = 'Success stories',
  className = '',
}) => {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const cls = `sm-${uid}`;

  const columnLists = Array.from({ length: Math.max(1, columns) }, () => []);
  stories.forEach((story, index) => columnLists[index % columnLists.length].push(story));

  const renderTrack = (list, width, heightPx, compact = false) =>
    [false, true].map((duplicate) =>
      list.map((story, index) => (
        <StoryCard
          key={`${duplicate ? 'dup' : 'orig'}-${story.id ?? index}`}
          story={story}
          width={width}
          height={heightPx}
          defaultBackground={defaultBackground}
          defaultTextColor={defaultTextColor}
          hidden={duplicate}
          compact={compact}
        />
      ))
    );

  const mask = fade
    ? `-webkit-mask-image: linear-gradient(180deg, transparent 0, #000 9%, #000 91%, transparent 100%);
       mask-image: linear-gradient(180deg, transparent 0, #000 9%, #000 91%, transparent 100%);`
    : '';
  const staggerOffset = stagger ? -(cardHeight + gap) / 2 : 0;
  // One loop travels the length of the un-duplicated track.
  const rowDuration = (stories.length * (mobileCard.width + mobileGap)) / rowSpeed;

  return (
    <section className={`${cls} relative min-w-0 ${className}`} aria-label={ariaLabel}>
      <style>{`
        .${cls} .${cls}-vert { display: flex; gap: ${columnGap}px; justify-content: center; height: ${height}; overflow: hidden; ${mask} }
        .${cls} .${cls}-col { width: ${cardWidth}px; overflow: hidden; }
        .${cls} .${cls}-track { display: flex; flex-direction: column; gap: ${gap}px; will-change: transform; }
        .${cls} .${cls}-up { animation: ${cls}-up linear infinite; }
        .${cls} .${cls}-down { animation: ${cls}-down linear infinite; }
        @keyframes ${cls}-up { from { transform: translateY(0); } to { transform: translateY(calc(-50% - ${gap / 2}px)); } }
        @keyframes ${cls}-down { from { transform: translateY(calc(-50% - ${gap / 2}px)); } to { transform: translateY(0); } }
        .${cls} .${cls}-row-wrap { display: none; overflow: hidden; }
        .${cls} .${cls}-row { display: flex; width: max-content; gap: ${mobileGap}px; will-change: transform; animation: ${cls}-x ${rowDuration}s linear infinite; }
        @keyframes ${cls}-x { from { transform: translateX(0); } to { transform: translateX(calc(-50% - ${mobileGap / 2}px)); } }
        @media (max-width: ${mobileBreakpoint}px) {
          .${cls} .${cls}-vert { display: none; }
          .${cls} .${cls}-row-wrap { display: block; }
        }
        @media (prefers-reduced-motion: reduce) {
          .${cls} .${cls}-track, .${cls} .${cls}-row { animation: none !important; }
        }
      `}</style>

      <div className={`${cls}-vert`}>
        {columnLists.map((list, index) => (
          <div key={index} className={`${cls}-col`}>
            <div
              className={`${cls}-track ${cls}-${
                alternate && index % 2 === 1 ? (direction === 'down' ? 'up' : 'down') : direction
              }`}
              style={{
                animationDuration: `${(list.length * (cardHeight + gap)) / columnSpeed}s`,
                marginTop: index % 2 === 0 ? staggerOffset : 0,
              }}
            >
              {renderTrack(list, cardWidth, cardHeight)}
            </div>
          </div>
        ))}
      </div>

      <div className={`${cls}-row-wrap`}>
        <div className={`${cls}-row`}>{renderTrack(stories, mobileCard.width, mobileCard.height, true)}</div>
      </div>
    </section>
  );
};

export default StoryMarquee;
